import { Objective } from "@/types/objective/objective-type";
import { DividerStyled, ObjectiveItemLeftStyled, ObjectiveItemRightStyled, ObjectivesContainerStyled, TitleStyled, ObjectiveItemRightBodyStyled } from "../../../style-component";
import { MissionComponentProps, MissionTypeCampaign, MissionTypeObjective, ObjectiveComponentProps } from "../../../missions-render-types";
import MissionSocialMedia from "../../objective-social-media/objective-social-media";
import ObjectiveSurvey from "../../objective-survey/objective-survey";
import RenderBenefit from "../../render-benefit/render-benefit";
import { useMission } from "@/hook/useMission/useMission";
import { LoaderContainer } from "@/pages/logged-page/style-component";
import { SpaceshipLoader } from "@/components/spaceship-loader/spaceship-loader";
import { useEffect, useRef, useState, memo, useCallback } from "react";
import RequirementsObjective from "../../../components/requirements-objective/requirements-objective";
import { TimeInfo } from "@/components/time-info/time-info";
import CountDownUpgradable from "@/components/countdown-upgradable/countdown-upgradable";
import { DescriptionBenefit } from "../../objective-benefit/style-component";
import ObjectiveBenefit from "../../objective-benefit/objective-benefit";
import CongratulateCard from "@/components/congratulate-card/congratulate-card";
import ExpiredMission from "@/components/expired-mission/expired-mission";
import { useUserContext } from "@/contexts/user/useUserContext";
import AlreadyHaveReward from "../../already-have-reward/already-have-reward";
import MissionTermsAndConditions from "../../mission-terms-and-conditions/mission-terms-and-conditions";
import LoginCard from "@/components/login-card/login-card";
import { useNavigate } from "react-router-dom";
import NextMissionButton from "@/components/next-mission-button/next-mission-button";


const TYPE_OBJECTIVE: Record<MissionTypeObjective, MissionTypeCampaign> = {
	FILL_OUT_SV: 'SURVEY',
	GET_SV_POINTS: 'SURVEY',
	GET_LIKES: 'SOCIAL_MEDIA',
	GET_COMMENTS: 'SOCIAL_MEDIA',
	GET_SHARES: 'SOCIAL_MEDIA',
	GET_VIEWS: 'SOCIAL_MEDIA',
	MAKE_POST: 'SOCIAL_MEDIA',
	CONNECT: 'SOCIAL_MEDIA',
	EXCHANGE: 'EXCHANGE',
};

const TYPE_OBJECTIVE_COMPONENT: Record<MissionTypeCampaign, React.FC<ObjectiveComponentProps>> = {
	SOCIAL_MEDIA: MissionSocialMedia,
	SURVEY: ObjectiveSurvey,
	EXCHANGE: ObjectiveBenefit,
};

const AllowedObjective: React.FC<ObjectiveComponentProps> = memo(({ objective, ...props }) => {

	const { type } = objective;
	const typeObjectiveCampaign = TYPE_OBJECTIVE?.[type?.split(':')[0] as MissionTypeObjective] ?? null;
	const Component = TYPE_OBJECTIVE_COMPONENT[typeObjectiveCampaign];

	return (
		<Component
			{...props}
			objective={objective}
		/>
	)
});

const RenderObjective: React.FC<ObjectiveComponentProps> = ({ objective, ...props }) => {
	const { type } = objective;
	const newType = type?.split(':')[0] ?? '';
	const typeObjectiveCampaign = TYPE_OBJECTIVE?.[newType as MissionTypeObjective] ?? null;
	if (!typeObjectiveCampaign) {
		return <div style={{ color: 'white' }}>Type objective {newType} not supported</div>;
	}
	return (
		<AllowedObjective
			{...props}
			objective={objective}
		/>
	)
}

const MissionChallenge: React.FC<MissionComponentProps> = ({ mission, participant, campaign, requirements }) => {
	const { description, name, objectives } = mission;
	const benefits = mission?.benefits ?? [];
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const now = Math.floor(Date.now() / 1000);
	const isExpiredMission = now > mission.end_date;
	const isComingSoon = now < mission.start_date;
	const { isConnect } = useUserContext();
	const { isCompleted, loadingProgress, missionState, getProgress } = useMission(mission, participant);
	const [completedRequirements, setCompletedRequirements] = useState(requirements?.length === 0);
	const haveObjectiveExchange = missionState?.objectives?.find((objective: Objective) => objective.type?.includes('EXCHANGE'))
	const haveSurvey = missionState?.objectives?.find((objective: Objective) => objective.type?.includes('FILL_OUT_SV'))
	const hideTitleObjective = missionState?.objectives?.some((objective: Objective) => objective.type?.includes('FILL_OUT_SV') || objective.type?.includes('EXCHANGE'));
	const [isClaimed, setIsClaimed] = useState<boolean>(false);
	const [isValidateClaim, setIsValidateClaim] = useState<boolean>(false);
	const alreadyHaveReward = isClaimed && !isCompleted;
	const tempCountDown = false;
	const showCountDown = tempCountDown && isConnect && completedRequirements && !isCompleted && !isExpiredMission && !haveObjectiveExchange && !haveSurvey && !alreadyHaveReward;

	const handleLogin = useCallback(() => {
		navigate('/login');
	}, [navigate]);

	const determineStatus = () => {
		if (!isClaimed && !haveObjectiveExchange && isCompleted) return 'pending_claim';
		if (isClaimed && isCompleted || alreadyHaveReward) return 'completed';
		if (now > mission.end_date) return 'expired';
		if (now < mission.start_date) return 'comingUp';
		return 'inCourse';
	}
	const status = determineStatus();

	const firstTime = useRef(true);
	useEffect(() => {
		if (!loadingProgress && missionState?.id) {
			if (firstTime.current) {
				firstTime.current = false;
				setLoading(false);
			}
		}
	}, [loadingProgress, missionState]);

	if (loading) {
		return (
			<LoaderContainer>
				<SpaceshipLoader height="150px" />
			</LoaderContainer>
		)
	}

	return (
		<ObjectivesContainerStyled>
			<ObjectiveItemLeftStyled>
				{name && <TitleStyled className="titleBenefit">{name}</TitleStyled>}
				<RenderBenefit
					benefits={benefits}
					participant={participant}
					isCompleted={isCompleted}
					mission={missionState}
					typeObjective={haveObjectiveExchange ? 'EXCHANGE' : ''}
					setIsClaimed={setIsClaimed}
					setIsValidateClaim={setIsValidateClaim}
				/>
				<TimeInfo
					isCampaign={false}
					endDate={missionState.end_date}
					startDate={missionState.start_date}
					status={status}
				/>
				{description && <DescriptionBenefit>{description}</DescriptionBenefit>}
				<MissionTermsAndConditions objectives={objectives} name={name}/>
				{haveObjectiveExchange && 
					<DescriptionBenefit style={{ display: 'none' }}>
						{benefits?.[0]?.reward?.description}
					</DescriptionBenefit>
				}
			</ObjectiveItemLeftStyled>
			<DividerStyled />
			<ObjectiveItemRightStyled>
				<LoginCard 
					onClick={handleLogin} 
					show={!isConnect}
				/>
				<ObjectiveItemRightBodyStyled $isConnectUser={isConnect}>
				{alreadyHaveReward && <AlreadyHaveReward />}
				{isCompleted && (
					<CongratulateCard
						type={TYPE_OBJECTIVE[missionState?.objectives?.[0]?.type?.split(':')[0] as MissionTypeObjective] || 'DEFAULT'}
					/>
				)}
				{!isCompleted && requirements?.length > 0 &&
					<RequirementsObjective
						requirements={requirements}
						setCompletedRequirements={setCompletedRequirements}
					/>}
				{!isCompleted && isExpiredMission && !loadingProgress ?
					<ExpiredMission />
					:
					<>
						{!hideTitleObjective && <TitleStyled>Objetivos</TitleStyled>}
						{missionState?.objectives?.map((objective: Objective, position: number) => (
							<RenderObjective
								key={objective.id}
								mission={mission}
								objective={objective}
								participant={participant}
								campaign={campaign}
								getProgress={getProgress}
								isExpiredMission={isExpiredMission}
								isComingSoon={isComingSoon}
								isCompleted={isCompleted}
								loadingProgress={loadingProgress}
								completedRequirements={completedRequirements === true}
								position={position + 1}
								disabledMission={alreadyHaveReward || isValidateClaim}
							/>
						))}
					</>
				}
				{((isClaimed && isCompleted) || alreadyHaveReward) && 
					<NextMissionButton missionId={mission.id} campaignId={campaign.id} />
				}
				{showCountDown && <CountDownUpgradable
					key={`countdown-${mission.id}-${isCompleted}`}
					onExecute={getProgress}
				/>}
				</ObjectiveItemRightBodyStyled>
			</ObjectiveItemRightStyled>
		</ObjectivesContainerStyled>
	)
}

export default MissionChallenge;